import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useArticleRecordings, useDeleteAudioRecording, useUpdateAudioRecording } from '@/queries/articles';
import type { AudioRecording } from '@/types/article';
import { AudioPlayer } from './AudioPlayer';
import HeadphonesIcon from '@/assets/common/headphones.svg?react';
import EditIcon from '@/assets/common/edit.svg?react';
import TrashIcon from '@/assets/common/trash.svg?react';
import styles from './RecordingsList.module.scss';

interface RecordingsListProps {
  articleId: string;
  isOwner: boolean;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function RecordingsList({ articleId, isOwner }: RecordingsListProps) {
  const { data: recordings, isLoading } = useArticleRecordings(articleId);
  const deleteRecording = useDeleteAudioRecording();
  const updateRecording = useUpdateAudioRecording();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleEdit = (recording: AudioRecording) => {
    setEditingId(recording.id);
    setEditTitle(recording.title);
    setEditDescription(recording.description || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditDescription('');
  };

  const handleSaveEdit = (recordingId: string) => {
    if (!editTitle.trim()) return;

    updateRecording.mutate(
      {
        articleId,
        recordingId,
        data: {
          title: editTitle.trim(),
          description: editDescription.trim() || null,
        },
      },
      {
        onSuccess: () => {
          handleCancelEdit();
        },
      }
    );
  };

  const handleDelete = (recordingId: string, title: string) => {
    if (confirm(`Are you sure you want to delete "${title}"? This action cannot be undone.`)) {
      deleteRecording.mutate({ articleId, recordingId });
    }
  };

  if (isLoading) {
    return (
      <div className={styles.recordings}>
        <div className={styles.recordings__loading}>Loading recordings...</div>
      </div>
    );
  }

  if (!recordings || recordings.length === 0) {
    return null;
  }

  return (
    <div className={styles.recordings}>
      <button
        className={styles.recordings__header}
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-expanded={!isCollapsed}
      >
        <div className={styles.recordings__headerContent}>
          <HeadphonesIcon className={styles.recordings__icon} />
          <h3 className={styles.recordings__title}>
            Audio Recordings ({recordings.length})
          </h3>
        </div>
        <span className={`${styles.recordings__chevron} ${isCollapsed ? styles['recordings__chevron--collapsed'] : ''}`}>
          &#9650;
        </span>
      </button>

      <AnimatePresence initial={false}>
        {!isCollapsed && (
          <motion.div
            className={styles.recordings__list}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            {recordings.map((recording) => (
              <div key={recording.id} className={styles.recordings__item}>
                {editingId === recording.id ? (
                  <div className={styles.recordings__editForm}>
                    <input
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Recording title"
                      className={styles.recordings__editInput}
                      autoFocus
                    />
                    <textarea
                      value={editDescription}
                      onChange={(e) => setEditDescription(e.target.value)}
                      placeholder="Description (optional)"
                      className={styles.recordings__editTextarea}
                      rows={2}
                    />
                    <div className={styles.recordings__editActions}>
                      <button
                        onClick={() => handleSaveEdit(recording.id)}
                        className={styles.recordings__saveButton}
                        disabled={!editTitle.trim() || updateRecording.isPending}
                      >
                        {updateRecording.isPending ? 'Saving...' : 'Save'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className={styles.recordings__cancelButton}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <AudioPlayer
                      articleId={articleId}
                      recordingId={recording.id}
                      title={recording.title}
                      durationSeconds={recording.duration_seconds}
                    />

                    {recording.description && (
                      <p className={styles.recordings__description}>
                        {recording.description}
                      </p>
                    )}

                    <div className={styles.recordings__meta}>
                      <span className={styles.recordings__size}>
                        {formatFileSize(recording.file_size_bytes)}
                      </span>

                      {isOwner && (
                        <div className={styles.recordings__actions}>
                          <button
                            onClick={() => handleEdit(recording)}
                            className={styles.recordings__actionButton}
                            title="Edit"
                          >
                            <EditIcon />
                          </button>
                          <button
                            onClick={() => handleDelete(recording.id, recording.title)}
                            className={`${styles.recordings__actionButton} ${styles['recordings__actionButton--danger']}`}
                            title="Delete"
                            disabled={deleteRecording.isPending}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
